import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from '@tanstack/react-router';

type MarkdownBodyProps = {
  content: string;
  className?: string;
};

/** Renders blog markdown with styled elements and internal link support. */
export function MarkdownBody({ content, className = '' }: MarkdownBodyProps) {
  return (
    <div className={`marketing-prose ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            if (href?.startsWith('/')) {
              return (
                <Link to={href} className="text-brand hover:underline">
                  {children}
                </Link>
              );
            }
            if (href?.startsWith('http')) {
              return (
                <a href={href} className="text-brand hover:underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              );
            }
            return <a href={href}>{children}</a>;
          },
          h1: ({ children }) => (
            <h1 className="mb-6 text-3xl font-semibold tracking-tight md:text-4xl">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-4 mt-10 scroll-mt-24 text-xl font-semibold tracking-tight md:text-2xl">{children}</h2>
          ),
          h3: ({ children }) => <h3 className="mb-3 mt-8 text-lg font-semibold">{children}</h3>,
          p: ({ children }) => <p className="mb-4 leading-relaxed text-text-secondary">{children}</p>,
          ul: ({ children }) => <ul className="mb-4 list-disc space-y-2 pl-6 text-text-secondary">{children}</ul>,
          ol: ({ children }) => <ol className="mb-4 list-decimal space-y-2 pl-6 text-text-secondary">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-text-primary">{children}</strong>,
          blockquote: ({ children }) => (
            <blockquote className="my-6 border-l-4 border-brand pl-4 italic text-text-secondary">{children}</blockquote>
          ),
          hr: () => <hr className="my-10 border-border" />,
          table: ({ children }) => (
            <div className="my-6 overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[480px] text-left text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="border-b border-border bg-bg-surface">{children}</thead>,
          th: ({ children }) => <th className="px-4 py-3 font-semibold">{children}</th>,
          td: ({ children }) => <td className="border-t border-border px-4 py-3 text-text-secondary">{children}</td>,
          code: ({ className: cn, children }) => {
            const isBlock = cn?.includes('language-');
            if (isBlock) {
              return (
                <pre className="my-6 overflow-x-auto rounded-xl bg-sidebar p-4 text-sm text-sidebar-text">
                  <code>{children}</code>
                </pre>
              );
            }
            return <code className="rounded bg-bg-surface px-1.5 py-0.5 text-[13px] text-text-primary">{children}</code>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
